import attachVideoElement from '../../common/Reporter/attachVideoElement';
import logger from '../../log';
import transformDuration from '../../common/Reporter/transformDuration';
import {
  BitmovinPlayer,
  BitmovinPlayerEvent
} from '@kollective-frontend/ksdk-api';
import { BitmovinReporterEvents, EventItem } from './BitmovinPlayerEvent';
import {
  dispatchIfValidDuration,
  dispatchPlayerEvent
} from '../../events/dispatch';
import {
  setupTechApiFunction,
  tearDownTechApiFunction,
  TechApi
} from '../../bootloader/tech';
import { CleanupMethod } from '../BaseInteraction';
import { TechApiState } from '../../reporter/constants';
import { TechEngagementMethods } from '../../common/Reporter/constants';

const log = logger.getLogger(__filename);

const { BITRATECHANGED } = TechEngagementMethods;

export const getDuration = (player: BitmovinPlayer): number => {
  const dur = player.getDuration();
  if (dur === Infinity) {
    return -1;
  }
  return transformDuration(dur);
};

export type OptionalStringUnkRecord = Record<string, unknown> | undefined;
export type StrNumRecord = Record<string, string | number>;
export type OptionalStringNumberRecord =
  | Record<string, number | undefined>
  | undefined;

export const getMetadataForEvent = (
  player: BitmovinPlayer,
  event: BitmovinPlayerEvent,
  info: TechApiState,
  metadata?: string
): OptionalStringNumberRecord => {
  switch (metadata) {
    case 'b':
      return getBitmovinBitrateEvent(player, event, info);
    case 'dur':
      return { dur: getDuration(player) };
    case 'pos':
      return { pos: player.getCurrentTime() };
    case 'rate':
      return { rate: player.getPlaybackSpeed() };
    default:
      return;
  }
};

export interface BitmovinAdapter {
  qualityChangeService?: {
    previousVideoBitrate?: number;
  };
}

export interface BitmovinAnalytics {
  internalAdapter?: BitmovinAdapter;
  analytics?: {
    adapter?: BitmovinAdapter;
  };
}

export interface BitmovinPlayerExtended extends BitmovinPlayer {
  analytics?: BitmovinAnalytics;
}

export function getBitmovinBitrateEvent(
  player: BitmovinPlayerExtended,
  event: BitmovinPlayerEvent,
  info: TechApiState
): OptionalStringNumberRecord | undefined {
  const b = bitmovinBitrate(player, info, event);
  return typeof b === 'number' ? { b } : undefined;
}

export function getAdapterBitrate(a?: BitmovinAdapter): number | undefined {
  return a?.qualityChangeService?.previousVideoBitrate;
}

export function bitmovinBitrate(
  player: BitmovinPlayerExtended,
  info: TechApiState,
  event?: BitmovinPlayerEvent
): number | undefined {
  const analytics = player?.analytics;
  const bitrate =
    event?.targetQuality?.bitrate ??
    getAdapterBitrate(analytics?.internalAdapter) ??
    getAdapterBitrate(analytics?.analytics?.adapter);
  info.lastBitrate = typeof bitrate === 'number' ? bitrate : info.lastBitrate;
  return typeof bitrate === 'number' ? bitrate : undefined;
}

export interface GenerateHandlerArgs {
  event: EventItem;
  player: BitmovinPlayer;
  info: TechApiState;
}
export const generateHandler =
  (args: GenerateHandlerArgs) =>
  (evt: BitmovinPlayerEvent): void => {
    const { event, player, info } = args;
    const { code: e, metadata } = event;
    const data = {
      e,
      ...getMetadataForEvent(player, evt, info, metadata)
    };
    log.debug('Handling Bitmovin event:', { data, event, evt });
    dispatchPlayerEvent(data);
  };

export interface AttachBitmovinEventsArgs {
  player: BitmovinPlayer;
  tech: TechApi;
}
type BitmovinEventHandler = (e: BitmovinPlayerEvent) => void;

const attachBitmovinEvents = (
  args: AttachBitmovinEventsArgs
): CleanupMethod => {
  const info: TechApiState = {};
  const player = args.player as BitmovinPlayerExtended;
  const getBitrate = () => bitmovinBitrate(player, info) ?? info.lastBitrate;
  setupTechApiFunction(args.tech, BITRATECHANGED, getBitrate);
  const element = player.getVideoElement();
  // Try setting the duration immediately upon attachment
  dispatchIfValidDuration(getDuration(player));
  const isVideoElement = element instanceof HTMLVideoElement;
  if (isVideoElement) {
    const event = BitmovinReporterEvents.VideoPlaybackQualityChanged;
    const handler = generateHandler({ event, player, info });
    player.on(event.name, handler);
    const cleanup = attachVideoElement(element, args.tech);
    return () => {
      cleanup();
      player.off(event.name, handler);
      tearDownTechApiFunction(args.tech, BITRATECHANGED);
    };
  } else {
    const handlers: Map<string, BitmovinEventHandler> = new Map();
    Object.getOwnPropertyNames(BitmovinReporterEvents)
      .map(key => BitmovinReporterEvents[key])
      .forEach((event: EventItem) => {
        const handler = generateHandler({ event, player, info });
        handlers.set(event.name, handler);
        player.on(event.name, handler);
      });
    return () => {
      handlers.forEach((handler, name) => player.off(name, handler));
      handlers.clear();
      tearDownTechApiFunction(args.tech, BITRATECHANGED);
    };
  }
};

export default attachBitmovinEvents;
